import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Bell, LogOut, Search, Users, Calendar, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

function CoordinatorDashboard() {
  const navigate = useNavigate();
  const [coordinator, setCoordinator] = useState(null);
  const [students, setStudents] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [searchRoll, setSearchRoll] = useState('');
  const [searchedStudent, setSearchedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    avgAttendance: 0,
    atRisk: 0
  });

  useEffect(() => {
    loadCoordinatorData();
  }, []);

  const loadCoordinatorData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/staff-login');
        return;
      }

      // Load coordinator profile
      const coordinatorDoc = await getDoc(doc(db, 'users', currentUser.email));
      if (coordinatorDoc.exists()) {
        const coordinatorData = coordinatorDoc.data();
        setCoordinator(coordinatorData);

        // Load students from assigned division
        const studentsQuery = query(
          collection(db, 'students'),
          where('division', '==', coordinatorData.assignedDivision)
        );
        const studentsSnapshot = await getDocs(studentsQuery);
        const studentsData = [];
        let totalAttendance = 0;
        let atRiskCount = 0;

        for (const studentDoc of studentsSnapshot.docs) {
          const studentData = studentDoc.data();
          
          // Load attendance
          const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
          const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;
          
          if (attendanceData) {
            totalAttendance += attendanceData.overallPercentage || 0;
            if (attendanceData.overallPercentage < 75) atRiskCount++;
          }

          studentsData.push({
            ...studentData,
            attendance: attendanceData
          });
        }

        setStudents(studentsData);
        setStats({
          totalStudents: studentsData.length,
          avgAttendance: studentsData.length > 0 ? Math.round(totalAttendance / studentsData.length) : 0,
          atRisk: atRiskCount
        });

        // Load pending leave requests from this division
        const leavesQuery = query(
          collection(db, 'leaves'),
          where('status', '==', 'pending')
        );
        const leavesSnapshot = await getDocs(leavesQuery);
        const divisionStudentRolls = studentsData.map(s => s.rollNumber);
        const pendingLeavesData = leavesSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(leave => divisionStudentRolls.includes(leave.rollNumber));

        setPendingLeaves(pendingLeavesData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading coordinator data:', error);
      setLoading(false);
    }
  };

  const handleSearchStudent = async () => {
    if (!searchRoll) return;

    try {
      const studentDoc = await getDoc(doc(db, 'students', searchRoll.toUpperCase()));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        
        // Load attendance
        const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
        const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;

        // Load IA tests
        const iaTest1Doc = await getDoc(doc(db, 'iaTests/test1', studentData.rollNumber));
        const iaTest2Doc = await getDoc(doc(db, 'iaTests/test2', studentData.rollNumber));

        setSearchedStudent({
          ...studentData,
          attendance: attendanceData,
          iaTest1: iaTest1Doc.exists() ? iaTest1Doc.data() : null,
          iaTest2: iaTest2Doc.exists() ? iaTest2Doc.data() : null
        });
      } else {
        alert('Student not found');
        setSearchedStudent(null);
      }
    } catch (error) {
      console.error('Error searching student:', error);
      alert('Error searching student');
    }
  };

  const handleLeaveAction = async (leaveId, status, comments = '') => {
    try {
      await updateDoc(doc(db, 'leaves', leaveId), {
        status: status,
        approvedBy: coordinator.email,
        approverComments: comments,
        approvalDate: new Date()
      });

      setPendingLeaves(pendingLeaves.filter(l => l.id !== leaveId));
      alert(`Leave ${status} successfully!`);
      loadCoordinatorData();
    } catch (error) {
      console.error('Error updating leave:', error);
      alert('Failed to update leave status');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Class Coordinator Dashboard</h1>
              <p className="text-sm text-gray-600">
                {coordinator?.name} | Division: {coordinator?.assignedDivision}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="relative p-2 hover:bg-gray-100 rounded-full">
                <Bell className="w-6 h-6 text-gray-700" />
                {pendingLeaves.length > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingLeaves.length}
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Roll Number Search</h2>
          <div className="flex space-x-3">
            <input
              type="text"
              value={searchRoll}
              onChange={(e) => setSearchRoll(e.target.value.toUpperCase())}
              placeholder="Enter Roll Number (e.g., FB107)"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
            />
            <button
              onClick={handleSearchStudent}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Search className="w-5 h-5" />
              <span>Search</span>
            </button>
          </div>
        </div>

        {/* Searched Student Details */}
        {searchedStudent && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {searchedStudent.firstName} {searchedStudent.surname} - {searchedStudent.rollNumber}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Attendance</h3>
                <p className={`text-3xl font-bold ${
                  searchedStudent.attendance?.overallPercentage >= 75 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {searchedStudent.attendance?.overallPercentage || 0}%
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">IA Test 1</h3>
                <p className="text-3xl font-bold text-gray-900">
                  {searchedStudent.iaTest1?.percentage || 0}%
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">IA Test 2</h3>
                <p className="text-3xl font-bold text-gray-900">
                  {searchedStudent.iaTest2?.percentage || 0}%
                </p>
              </div>
            </div>
            <button
              onClick={() => setSearchedStudent(null)}
              className="mt-4 text-blue-600 hover:underline"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Division Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Total Students</h3>
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalStudents}</p>
            <p className="text-sm text-gray-500 mt-1">Division {coordinator?.assignedDivision}</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Avg Attendance</h3>
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.avgAttendance}%</p>
            <p className="text-sm text-gray-500 mt-1">Division average</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Students at Risk</h3>
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-600">{stats.atRisk}</p>
            <p className="text-sm text-gray-500 mt-1">Below 75% attendance</p>
          </div>
        </div>

        {/* Pending Leave Requests */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Pending Leave Requests</h2>
            <Calendar className="w-6 h-6 text-orange-600" />
          </div>

          {pendingLeaves.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No pending leave requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingLeaves.map(leave => (
                <div key={leave.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-blue-600">{leave.rollNumber}</span>
                        <span className="text-gray-600">|</span>
                        <span className="text-gray-900">{leave.studentName}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p>
                          <span className="font-medium">Type:</span>{' '}
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded capitalize">
                            {leave.leaveType}
                          </span>
                        </p>
                        <p>
                          <span className="font-medium">Duration:</span> {leave.fromDate} to {leave.toDate}
                        </p>
                        <p>
                          <span className="font-medium">Reason:</span> {leave.reason}
                        </p>
                        <p className="text-xs text-gray-500">
                          Submitted: {new Date(leave.submittedAt?.toDate()).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {leave.documentUrl && (
                      <button
                        onClick={() => window.open(leave.documentUrl, '_blank')}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        View Document
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleLeaveAction(leave.id, 'approved')}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => {
                        const comments = prompt('Please provide reason for rejection:');
                        if (comments) {
                          handleLeaveAction(leave.id, 'rejected', comments);
                        }
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Students at Risk */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Students at Risk (&lt;75% Attendance)</h2>
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>

          {students.filter(s => s.attendance?.overallPercentage < 75).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-300" />
              <p>All students have acceptable attendance</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {students
                .filter(s => s.attendance?.overallPercentage < 75)
                .map(student => (
                  <div key={student.rollNumber} className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{student.rollNumber}</p>
                        <p className="text-sm text-gray-600">
                          {student.firstName} {student.surname}
                        </p>
                      </div>
                      <span className="text-xl font-bold text-red-600">
                        {student.attendance?.overallPercentage}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">Batch: {student.batch}</p>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoordinatorDashboard;